import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, push, set } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function EarningsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('upi');
  const [upiId, setUpiId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  useEffect(() => {
    if (!user || !user.name) return;
    const paymentsRef = ref(db, 'payments/');
    const unsubP = onValue(paymentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map(key => ({ id: key, ...data[key] })).filter(p => p.driver === user.name);
        setPayments(list);
      }
    });
    const withdrawalsRef = ref(db, 'withdrawals/');
    const unsubW = onValue(withdrawalsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map(key => ({ id: key, ...data[key] }))
          .filter(w => w.driverPhone === user.phone)
          .sort((a, b) => (b.requestedAt || '').localeCompare(a.requestedAt || ''));
        setWithdrawals(list);
      }
    });
    return () => { unsubP(); unsubW(); };
  }, [user]);

  const paidPayments = payments.filter(p => p.status === 'paid');
  const cashPendingPayments = payments.filter(p => p.status === 'cash_pending');
  const totalCollected = paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalPendingCash = cashPendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalWithdrawn = withdrawals.filter(w => w.status === 'completed').reduce((sum, w) => sum + (w.amount || 0), 0);
  const availableToWithdraw = Math.max(0, totalCollected - totalWithdrawn);

  const monthlyTotals = (() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ label: MONTH_LABELS[d.getMonth()], month: d.getMonth(), year: d.getFullYear(), total: 0 });
    }
    [...paidPayments, ...cashPendingPayments].forEach(p => {
      if (!p.timestamp) return;
      const d = new Date(p.timestamp);
      const match = months.find(m => m.month === d.getMonth() && m.year === d.getFullYear());
      if (match) match.total += (p.amount || 0);
    });
    return months;
  })();
  const maxMonthly = Math.max(...monthlyTotals.map(m => m.total), 1);

  const submitWithdrawal = async () => {
    const amt = parseInt(withdrawAmount);
    if (!amt || amt < 100) { alert('Minimum withdrawal amount is ₹100.'); return; }
    if (amt > availableToWithdraw) { alert('You cannot withdraw more than your available balance.'); return; }
    if (withdrawMethod === 'upi' && !upiId.trim()) { alert('Please enter your UPI ID.'); return; }

    setSubmitting(true);
    try {
      const newRef = push(ref(db, 'withdrawals/'));
      await set(newRef, {
        amount: amt, method: withdrawMethod, upiId: withdrawMethod === 'upi' ? upiId.trim() : null,
        driverName: user.name, driverPhone: user.phone, status: 'pending', requestedAt: new Date().toISOString(),
      });
      setSubmitting(false);
      setShowModal(false);
      setWithdrawAmount('');
      alert('Withdrawal request submitted. Processed within 24-48 hours.');
    } catch (error) {
      setSubmitting(false);
      alert('Error: ' + error.message);
    }
  };

  if (!user) return null;

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>💰 {t('myEarnings')}</h2>

        <div style={styles.statsGrid}>
          <div style={styles.statBox}><div style={styles.statVal}>₹{totalCollected}</div><div style={styles.statLbl}>Collected</div></div>
          <div style={styles.statBox}><div style={styles.statVal}>{paidPayments.length}</div><div style={styles.statLbl}>Rides given</div></div>
          <div style={styles.statBox}><div style={styles.statVal}>₹{totalWithdrawn}</div><div style={styles.statLbl}>Withdrawn</div></div>
          <div style={styles.statBox}><div style={{ ...styles.statVal, color: '#f0a53a' }}>₹{totalPendingCash}</div><div style={styles.statLbl}>Cash pending</div></div>
        </div>

        <div style={styles.chartCard}>
          <h3 style={{ margin: '0 0 14px', color: '#1a1a2e' }}>Monthly earnings</h3>
          <div style={styles.chartBars}>
            {monthlyTotals.map((m, i) => (
              <div key={i} style={styles.chartBarWrap}>
                <div style={{ ...styles.chartBar, height: Math.max(4, (m.total / maxMonthly) * 80), background: i === monthlyTotals.length - 1 ? '#3a8fd0' : '#c7dff0' }}></div>
                <div style={styles.chartBarLabel}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>

        <h3 style={{ marginBottom: 10, color: '#1a1a2e' }}>Recent rides</h3>
        {[...paidPayments, ...cashPendingPayments].length === 0 ? (
          <p style={{ color: '#9499b5', textAlign: 'center' }}>No rides yet</p>
        ) : (
          [...paidPayments, ...cashPendingPayments].slice(0, 10).map(p => (
            <div key={p.id} style={styles.rideRow}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{p.from} → {p.to}</div>
                <div style={{ fontSize: 11, color: '#9499b5' }}>{p.passengerName}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, color: '#1a1a2e' }}>₹{p.amount}</div>
                <div style={{ fontSize: 10, color: p.status === 'paid' ? '#00c896' : '#f0a53a' }}>{p.status === 'paid' ? 'Paid' : 'Cash pending'}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={styles.withdrawBar}>
        <div>
          <div style={{ fontSize: 11, color: '#9499b5' }}>Available to withdraw</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>₹{availableToWithdraw}</div>
        </div>
        <button style={styles.withdrawBtn} onClick={() => setShowModal(true)}>{t('withdraw')}</button>
      </div>

      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#1a1a2e' }}>Withdraw funds</h3>
            <p style={{ fontSize: 13, color: '#5a5f7d' }}>Available: ₹{availableToWithdraw}</p>
            <input style={styles.input} placeholder="Amount (min ₹100)" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} type="number" />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, marginBottom: 10 }}>
              <button style={withdrawMethod === 'upi' ? styles.methodChipActive : styles.methodChip} onClick={() => setWithdrawMethod('upi')}>UPI</button>
              <button style={withdrawMethod === 'bank' ? styles.methodChipActive : styles.methodChip} onClick={() => setWithdrawMethod('bank')}>Bank</button>
            </div>
            {withdrawMethod === 'upi' && (
              <input style={styles.input} placeholder="yourname@upi" value={upiId} onChange={e => setUpiId(e.target.value)} />
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.submitBtn} onClick={submitWithdrawal} disabled={submitting}>{submitting ? 'Submitting...' : 'Submit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif', paddingBottom: 80 },
  container: { maxWidth: 800, margin: '0 auto', padding: '30px 20px 20px' },
  pageTitle: { color: '#1a1a2e', marginBottom: 20 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 },
  statBox: { border: '1px solid #eef1f8', borderRadius: 12, padding: 14, textAlign: 'center' },
  statVal: { fontSize: 18, fontWeight: 700, color: '#3a8fd0' },
  statLbl: { fontSize: 11, color: '#9499b5', marginTop: 2 },
  chartCard: { border: '1px solid #eef1f8', borderRadius: 14, padding: 18, marginBottom: 20 },
  chartBars: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 100 },
  chartBarWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 },
  chartBar: { width: 20, borderRadius: 4, marginBottom: 6 },
  chartBarLabel: { fontSize: 10, color: '#9499b5' },
  rideRow: { display: 'flex', justifyContent: 'space-between', border: '1px solid #eef1f8', borderRadius: 10, padding: 12, marginBottom: 8 },
  withdrawBar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1a2e', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  withdrawBtn: { background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 },
  modalCard: { background: '#ffffff', borderRadius: 16, padding: 20, maxWidth: 380, width: '100%' },
  input: { width: '100%', height: 44, borderRadius: 8, border: '1.5px solid #d9edfa', padding: '0 12px', fontSize: 14, boxSizing: 'border-box', marginTop: 10 },
  methodChip: { flex: 1, height: 38, borderRadius: 8, border: '1px solid #d9edfa', background: '#f7f9fc', cursor: 'pointer' },
  methodChipActive: { flex: 1, height: 38, borderRadius: 8, border: '1px solid #3a8fd0', background: '#3a8fd0', color: '#fff', cursor: 'pointer' },
  cancelBtn: { flex: 1, height: 44, borderRadius: 10, border: '1px solid #d9edfa', background: '#fff', cursor: 'pointer', fontWeight: 600 },
  submitBtn: { flex: 1, height: 44, borderRadius: 10, border: 'none', background: '#3a8fd0', color: '#fff', cursor: 'pointer', fontWeight: 700 },
};